import * as React from "react"
import { Card, Dialog, DialogContent, IconButton, Box, makeStyles, Theme } from "@material-ui/core"
import { Play, X as CloseIcon } from "react-feather"

type Video = {
  id: string
  title: string
  link: string
  thumbnailUrl?: string // Optional override
}

type HelpVideoProps = {
  video: Video
}

const YT_RED = "#FF0000"
const YT_RED_HOVER = "#CC0000"
const YT_RED_ACTIVE = "#B80000"

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    overflow: "hidden",
  },
  chipBox: {
    display: "inline-block",
    marginRight: theme.spacing(1),
  },
  thumbBox: {
    position: "relative",
    paddingTop: "56.25%",
    overflow: "hidden",
  },
  thumbImg: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center",
    transform: "scale(1.1)", // zoom in slightly to get rid of black bars on top and bottom
  },
  playButtonBox: {
    position: "absolute",
    inset: 0,
    margin: "auto",
    width: 88,
    height: 88,
    opacity: 0.9,
    background: "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  playButton: {
    backgroundColor: YT_RED,
    color: "#fff",
    width: 88,
    height: 56,
    borderRadius: 8,
    boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
    "&:hover": { backgroundColor: YT_RED_HOVER },
    "&:active": { backgroundColor: YT_RED_ACTIVE },
  },
  dialogContent: {
    padding: 0,
  },
  iframeBox: {
    position: "relative",
    paddingTop: "51.25%", // the video's aspect ratio
  },
  iframe: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    border: 0,
  },
  closeButton: {
    color: theme.palette.grey[900],
    background: theme.palette.background.paper,
    "&:hover": {
      background: theme.palette.grey[200],
    },
  },
}))

const extractYouTubeId = (url: string): string | null => {
  try {
    const u = new URL(url)
    // youtu.be/<id>
    if (u.hostname.includes("youtu.be")) {
      return u.pathname.slice(1) || null
    }
    // youtube.com/watch?v=<id>
    if (u.searchParams.get("v")) {
      return u.searchParams.get("v")
    }
    // /embed/<id> or /shorts/<id> or /live/<id>
    const path = u.pathname.split("/").filter(Boolean)
    const idx = path.findIndex((p) => ["embed", "shorts", "live"].includes(p))
    if (idx >= 0 && path[idx + 1]) return path[idx + 1]

    // Fallback: last segment might be the id
    if (path.length) return path[path.length - 1]
  } catch {
    // ignore
  }
  return null
}

const getThumbnailUrl = (id: string, resolution: "maxres" | "hq") => {
  return `https://img.youtube.com/vi/${id}/${resolution}default.jpg`
}

type YouTubeThumbnailProps = {
  videoId: string
  onClick?: () => void
}

const YouTubeThumbnail = ({ videoId, onClick }: YouTubeThumbnailProps) => {
  const classes = useStyles()
  const [src, setSrc] = React.useState(getThumbnailUrl(videoId, "maxres"))

  const handleError = React.useCallback(() => {
    if (!src.includes("hqdefault")) {
      setSrc(getThumbnailUrl(videoId, "hq"))
    }
  }, [src, videoId])

  return (
    <Box className={`${classes.thumbBox}`} onClick={onClick}>
      <img src={src} alt={"Starbrix help video"} loading="lazy" onError={handleError} className={classes.thumbImg} />
      <Box className={classes.playButtonBox} aria-label="Play video">
        <IconButton aria-label="Play" className={classes.playButton}>
          <Play fontSize="large" />
        </IconButton>
      </Box>
    </Box>
  )
}

const HelpVideo = (props: HelpVideoProps) => {
  const { video } = props
  const [open, setOpen] = React.useState(false)
  const classes = useStyles()

  const id = React.useMemo(() => extractYouTubeId(video.link), [video.link])

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  return (
    <>
      <Card variant="outlined" className={classes.card}>
        {id ? <YouTubeThumbnail videoId={id} onClick={handleOpen} /> : null}
      </Card>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" aria-labelledby="video-dialog-title">
        <Box display={"flex"} justifyContent={"flex-end"}>
          <IconButton aria-label="Close" className={classes.closeButton} onClick={handleClose}>
            <CloseIcon size={24} />
          </IconButton>
        </Box>

        <DialogContent className={classes.dialogContent}>
          <Box className={classes.iframeBox}>
            {id && (
              <iframe
                title={video.title}
                src={`https://www.youtube.com/embed/${id}?autoplay=1&rel=0`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className={classes.iframe}
              />
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default HelpVideo
